import { IconButton } from '@material-ui/core'
import {
  ArrowDownwardOutlined,
  ArrowUpwardOutlined,
  CloseOutlined,
} from '@material-ui/icons'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { useBomItemDetails } from 'features/BillOfMaterials/components/useBomItemDetails'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { isEqual } from 'lodash'
import { BoMItemType } from 'model/Project/BoMItemRow'
import { useCallback, useEffect } from 'react'
import { useAppSelector } from 'store/configureStore'
import { Key } from 'utils/keyCodes'
import { useBomItemDetailsDrawerStyles } from './BomItemDetailsDrawer.styles'

const titleTranslationKey = {
  [BoMItemType.project]: 'project:project',
  [BoMItemType.row]: 'project:part-type',
  [BoMItemType.assembly]: 'project:assembly',
  [BoMItemType.assemblyHeader]: 'project:assembly',
  [BoMItemType.materialHeader]: 'project:material',
}

export function BomItemDetailsDrawerHeader(props: {
  bomItemId: string
  bomItemType: BoMItemType
  handleClose: () => void
}) {
  const classes = useBomItemDetailsDrawerStyles()

  const { previousBomItem, nextBomItem } = useAppSelector(
    projectSelectors.previousAndNextBomItemIdsSelector(props.bomItemId),
    isEqual
  )

  const { handleOpenBomItemDetails } = useBomItemDetails()

  const openNextBomItem = useCallback(() => {
    if (!nextBomItem) {
      return
    }
    handleOpenBomItemDetails(
      nextBomItem.bomItemId,
      nextBomItem.bomItemType,
      true
    )
  }, [handleOpenBomItemDetails, nextBomItem])

  const openPreviousBomItem = useCallback(() => {
    if (!previousBomItem) {
      return
    }
    handleOpenBomItemDetails(
      previousBomItem.bomItemId,
      previousBomItem.bomItemType,
      true
    )
  }, [handleOpenBomItemDetails, previousBomItem])

  const handleClose = () => {
    setTimeout(() => props.handleClose())
  }

  const handleArrows = useCallback(
    (e) => {
      if (e.key === Key.ArrowDown) {
        e.preventDefault()
        e.stopPropagation()
        openNextBomItem()
      }

      if (e.key === Key.ArrowUp) {
        e.preventDefault()
        e.stopPropagation()
        openPreviousBomItem()
      }
    },
    [openNextBomItem, openPreviousBomItem]
  )

  const closeOnEscape = useCallback(
    (e: globalThis.KeyboardEvent) => {
      if (e.key === Key.Escape) {
        props.handleClose()
        return
      }
      handleArrows(e)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleClose]
  )

  useEffect(() => {
    // const openTimeout = setTimeout(() => setOpen(true))
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      // clearTimeout(openTimeout)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [closeOnEscape])

  return (
    <>
      <LocalizedTypography
        translationKey={
          titleTranslationKey[props.bomItemType] ??
          titleTranslationKey[BoMItemType.project]
        }
        variant="h6"
      />
      <IconButton
        className={classes.closeButton}
        onClick={props.handleClose}
        size="small"
      >
        <CloseOutlined />
      </IconButton>
      <IconButton
        className={classes.previousItemButton}
        onClick={openPreviousBomItem}
        size="small"
        disabled={!previousBomItem}
      >
        <ArrowUpwardOutlined />
      </IconButton>
      <IconButton
        className={classes.nextItemButton}
        onClick={openNextBomItem}
        size="small"
        disabled={!nextBomItem}
      >
        <ArrowDownwardOutlined />
      </IconButton>
    </>
  )
}
